// Shared trip dates and time helper utilities

export const TRIP_START_DATE = "2026-06-13";

export const TRIP_DATES = {
  "Saturday 6/13": "2026-06-13",
  "Sunday 6/14": "2026-06-14",
  "Monday 6/15": "2026-06-15",
  "Tuesday 6/16": "2026-06-16",
  "Wednesday 6/17": "2026-06-17",
  "Thursday 6/18": "2026-06-18",
  "Friday 6/19": "2026-06-19",
  "Saturday 6/20": "2026-06-20",
  "Sunday 6/21": "2026-06-21"
};

export const DAYS = [
  "Saturday 6/13",
  "Sunday 6/14",
  "Monday 6/15",
  "Tuesday 6/16",
  "Wednesday 6/17",
  "Thursday 6/18",
  "Friday 6/19",
  "Saturday 6/20"
];

/**
 * Resolves a date string (either YYYY-MM-DD or textual date) to a standard trip day label.
 * 
 * @param {string} dateStr The date string to convert
 * @returns {string} The matched trip day label, or empty string if not found
 */
export const dateToTripDay = (dateStr) => {
  if (!dateStr) return "";
  
  // 1. Try exact key matching first if it's dynamic format like YYYY-MM-DD
  const found = Object.entries(TRIP_DATES).find(([_, date]) => date === dateStr);
  if (found) return found[0];
  
  // 2. Check if the string matches one of the day keys directly
  if (TRIP_DATES[dateStr]) return dateStr;
  
  // 3. Fallback to substring matching for flexible user input
  const lower = dateStr.toLowerCase();
  if (lower.includes("6/13") || lower.includes("06-13") || lower.includes("jun 13")) return "Saturday 6/13";
  if (lower.includes("6/14") || lower.includes("06-14") || lower.includes("jun 14")) return "Sunday 6/14";
  if (lower.includes("6/15") || lower.includes("06-15") || lower.includes("jun 15")) return "Monday 6/15";
  if (lower.includes("6/16") || lower.includes("06-16") || lower.includes("jun 16")) return "Tuesday 6/16";
  if (lower.includes("6/17") || lower.includes("06-17") || lower.includes("jun 17")) return "Wednesday 6/17";
  if (lower.includes("6/18") || lower.includes("06-18") || lower.includes("jun 18")) return "Thursday 6/18";
  if (lower.includes("6/19") || lower.includes("06-19") || lower.includes("jun 19")) return "Friday 6/19";
  if (lower.includes("6/20") || lower.includes("06-20") || lower.includes("jun 20")) return "Saturday 6/20";
  if (lower.includes("6/21") || lower.includes("06-21") || lower.includes("jun 21")) return "Sunday 6/21";
  
  return "";
};

/**
 * Parses a raw date and/or time string and returns parsed structured components.
 * Formats time string to standard 12-hour AM/PM.
 * 
 * @param {string} rawTimeStr Raw datetime string (e.g. "14:55, Jul 16", "2026-06-13T14:55")
 * @returns {object} { time: string, date: string }
 */
export const parseTimeAndDate = (rawTimeStr) => {
  if (!rawTimeStr) return { time: "TBD", date: "" };

  let timePart = "";
  let datePart = "";

  const trimmed = rawTimeStr.trim();

  // Case 1: Comma separated (e.g., "14:55, Jul 16" or "2:55 PM, Jun 13")
  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map(s => s.trim());
    if (parts[0].includes(':')) {
      timePart = parts[0];
      datePart = parts[1] || "";
    } else if (parts[1] && parts[1].includes(':')) {
      timePart = parts[1];
      datePart = parts[0];
    }
  }

  // Case 2: ISO style YYYY-MM-DD[T ]HH:MM
  if (!timePart) {
    const isoMatch = trimmed.match(/(\d{4}-\d{2}-\d{2})[T ](\d{1,2}):(\d{2})(?::\d{2})?/);
    if (isoMatch) {
      datePart = isoMatch[1];
      timePart = `${isoMatch[2]}:${isoMatch[3]}`;
    }
  }

  // Case 3: Fallback regex to find time HH:MM (AM/PM)
  if (!timePart) {
    const timeMatch = trimmed.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?/i);
    if (timeMatch) {
      timePart = timeMatch[0];
      datePart = trimmed.replace(timeMatch[0], "").replace(/^[TZ\s,:]+|[TZ\s,:]+$/g, "").trim();
    }
  }

  if (!timePart) {
    return { time: trimmed, date: "" };
  }

  // Format to 12-hour AM/PM
  let formattedTime = timePart;
  if (!timePart.toLowerCase().includes('am') && !timePart.toLowerCase().includes('pm')) {
    const match = timePart.match(/(\d{1,2}):(\d{2})/);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = match[2];
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      formattedTime = `${hours}:${minutes} ${ampm}`;
    }
  }

  return { time: formattedTime, date: datePart };
};
